import React from 'react';
import axios from 'axios';
import { of, from, merge, EMPTY, BehaviorSubject } from 'rxjs';
import { catchError, mergeMap, filter, take, map } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import { FormattedMessage } from 'react-intl';

import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { initiateRestoreDefaultScale } from '../shipstation-connect/ss-connect.actions';
import { subscribeToSellerChannelSuccess } from '../pusher/pusher.actions';

import * as AppActions from '../../store/app/app.actions';
import { APP_INITIALIZATION } from './base-actions';
import { messages } from './messages';

import * as ConnectActions from '../shipstation-connect/ss-connect.actions';
import { getServiceCollections } from '../seller/seller.api';
import {
  refreshServiceCollectionsSuccess,
  updateSellerInventoryWarehouses,
  updateSellerInventoryWarehousesLocations,
  updateSellerTotes,
  setSellerTotesError,
} from '../seller/seller.actions';
import { requestGetSuppliesProductsSuccess, requestGetSuppliesProductsError } from '../supplies/supplies.actions';
import { getSuppliesProducts } from '../supplies/supplies.api';
import { getInventoryWarehouses, getInventoryWarehouseLocations, getAllTotes } from '../inventory/inventory.api';
import { retryWithBackoff, isAuthError } from '../../services/retry-with-backoff';
import { PusherService, buildDefaultConfigs, PRIMARY_PUSHER_KEY } from '../../services/pusher-service';
import { compareVersion } from '../../services/compare-version';
import { LogService } from '../../services/log-service';
import { subscribedChannelsStore } from '../pusher/subscribed-channels-store';
import { batchAuthenticate, postMessage } from '../pusher/pusher.api';
import { getTooLargeMessage } from '../pusher/fallback-api';
import { GuidService } from '../../ui-components/services/guid-service';
import { noop } from 'ui-components/default-props/noop';
import { getFlagsFromLdClient } from '../../feature-flags/redux/init';

const batchAuthenticateApi = (requests) => {
  if (!requests.length) {
    return new Promise((_, reject) => {
      reject(new Error('No requests. Cannot batch authenticate.'));
    });
  }
  return batchAuthenticate(requests).then((response) => response?.data?.responses ?? []);
};

const authenticatePusher = ({ options, socketId, channelName, pusherAppKey }) => {
  return axios.post(
    '/auth/pusher',
    {
      type: options.auth.params.type,
      socket_id: socketId,
      channel_name: channelName,
      pusherAppKey,
      pusherInstanceName: pusherAppKey === PRIMARY_PUSHER_KEY ? 'primary' : 'failover',
    },
    {
      headers: {
        ContentType: 'application/x-www-form-urlencoded',
      },
    },
  );
};

const showAppInitFailureNotification = (label, contentMessage) => {
  LogService.logInfo(`App init: ${label} failed after retries`);

  NotificationService.showNotification({
    notificationType: NOTIFICATION_TYPE_ERROR,
    title: <FormattedMessage {...messages.appInitFailureTitle} />,
    content: <FormattedMessage {...contentMessage} />,
    dontHide: true,
  });
};

const delayedAppStartupRequests = () => {
  return merge(
    from(getServiceCollections()).pipe(
      mergeMap((serviceCollections) => {
        return of(refreshServiceCollectionsSuccess(serviceCollections));
      }),
      catchError(() => EMPTY),
    ),
    retryWithBackoff({
      apiFactory: () => from(getInventoryWarehouses().promise),
    }).pipe(
      mergeMap((inventoryWarehouses) => {
        return of(updateSellerInventoryWarehouses(inventoryWarehouses.data ?? []));
      }),
      catchError((error) => {
        LogService.logInfo(`App init: getInventoryWarehouses failed - status: ${error?.response?.status}`);
        return of(updateSellerInventoryWarehouses([]));
      }),
    ),
    from(getInventoryWarehouseLocations().promise).pipe(
      mergeMap((inventoryLocations) => {
        return of(updateSellerInventoryWarehousesLocations(inventoryLocations.data ?? []));
      }),
      catchError(() => EMPTY),
    ),
    retryWithBackoff({
      apiFactory: () => from(getAllTotes().promise),
    }).pipe(
      mergeMap((totes) => of(updateSellerTotes(totes || []))),
      catchError((error) => {
        if (isAuthError(error)) {
          return of(updateSellerTotes([]));
        }
        showAppInitFailureNotification('getAllTotes', messages.totesLoadFailure);
        return of(setSellerTotesError(error.message || 'Failed to load totes'));
      }),
    ),
    getSuppliesProducts().pipe(
      mergeMap((products) => of(requestGetSuppliesProductsSuccess(products))),
      catchError((error) => {
        LogService.logInfo('App init: getSuppliesProducts failed', error);
        return of(requestGetSuppliesProductsError(error?.message || 'Failed to load supplies products'));
      }),
    ),
  );
};

export const appInitialization = (action$) => {
  return action$.pipe(
    ofType(APP_INITIALIZATION),
    mergeMap(({ dispatch }) => {
      // reads value out of local storage and puts it in a reducer.
      dispatch(initiateRestoreDefaultScale());

      window.renderApp?.();
      return merge(of(AppActions.appRenderSuccess()), delayedAppStartupRequests());
    }),
  );
};

export const initializePusher = (action$) => {
  return action$.pipe(
    ofType(APP_INITIALIZATION),
    mergeMap(({ dispatch, initialData }) => {
      const { seller, pusherData, workstations, currentUser } = initialData;
      const { sellerId } = seller;
      const { pusherApps, primaryPusherApp } = pusherData;
      const { userId } = currentUser;

      const defaultPusherConfigs = buildDefaultConfigs();

      const mainChannelCount$ = new BehaviorSubject(0);

      const onMainSellerChannelSubscribe = ({ isSubscribed, isError }) => {
        if (isSubscribed || isError) {
          mainChannelCount$.next(mainChannelCount$.value + 1);
        }
      };

      const workstationsSupportReplyTo = workstations.some((w) => compareVersion(w.version, '4.10.0') > -1);
      const replyToId = workstationsSupportReplyTo ? `${userId}_${GuidService.newGuid()}` : undefined;

      return from(
        PusherService.initialize({
          sellerId,
          replyToId,
          authenticateWithServer: authenticatePusher,
          pusherInstanceConfigs: pusherApps || defaultPusherConfigs,
          logger: LogService,
          externalStore: subscribedChannelsStore,
          onHttpPush: postMessage,
          activeSocketKey: primaryPusherApp,
          batchAuthenticateApi,
          httpFallback: getTooLargeMessage,
          onMainSellerChannelSubscribe,
          shouldAllowResubscribe: true,
          onReplyToChannelSubscribe: noop,
          pusherChannelSubscriptionTimeoutMs: 20000,
        }),
      ).pipe(
        mergeMap((basicChannelInfo) => {
          const activeSockets = PusherService.getConnectedSockets().length;

          return mainChannelCount$.pipe(
            filter((count) => count === activeSockets),
            take(1),
            map(() => {
              const { primaryPusherApp: pusherAppKey } = getFlagsFromLdClient();
              PusherService.setActiveSocketKey(pusherAppKey);
              PusherService.setShouldUseHttpTrigger(true);
              PusherService.setShouldUseReplyToChannel(true);

              dispatch(ConnectActions.getWorkstationsSuccess(workstations));
              return subscribeToSellerChannelSuccess(basicChannelInfo);
            }),
          );
        }),
        catchError((error) => {
          LogService.logError(new Error('Failed to initialize Pusher', { cause: error }));
          return EMPTY;
        }),
      );
    }),
  );
};
